import React, { createContext, useContext, useReducer, useEffect } from 'react';
import authService from '../services/authService';

// Authentication action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  TOKEN_REFRESH: 'TOKEN_REFRESH',
  SET_USER: 'SET_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LOADING: 'SET_LOADING'
};

// Initial authentication state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  token: null,
  refreshToken: null,
  lastActivity: null
};

// Authentication reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        error: null,
        lastActivity: new Date().toISOString()
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        refreshToken: null,
        error: action.payload.error,
        lastActivity: null
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        lastActivity: null
      };

    case AUTH_ACTIONS.TOKEN_REFRESH:
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken || state.refreshToken,
        lastActivity: new Date().toISOString()
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: !!action.payload.user
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload.loading
      };

    default:
      return state;
  }
};

// Create authentication context
const AuthContext = createContext();

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: { loading: true } });

        // Check if user has stored authentication data
        const isAuth = authService.isAuthenticated();
        const userData = authService.getUserData();
        const token = authService.getAccessToken();
        const refreshToken = authService.getRefreshToken();

        if (isAuth && userData && token) {
          // Verify token with server
          const verificationResult = await authService.verifyToken();
          
          if (verificationResult.success) {
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                user: userData,
                token: token,
                refreshToken: refreshToken
              }
            });
          } else {
            // Token invalid, clear stored data
            authService.clearTokens();
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
          }
        } else {
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        authService.clearTokens();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      } finally {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: { loading: false } });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });

      const result = await authService.login(email, password);

      if (result.success && result.data) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: result.data.user,
            token: result.data.accessToken,
            refreshToken: result.data.refreshToken
          }
        });
        return { success: true, data: result.data };
      } else {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: result.error?.message || 'Login failed' }
        });
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = error.message || 'Network error occurred';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, error: { message: errorMessage } };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: { loading: true } });

      const result = await authService.register(userData);

      if (result.success && result.data) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: result.data.user,
            token: result.data.accessToken,
            refreshToken: result.data.refreshToken
          }
        });
        return { success: true, data: result.data };
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: { loading: false } });
        return { success: false, error: result.error };
      }
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: { loading: false } });
      return { success: false, error: { message: error.message } };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Forgot password function
  const forgotPassword = async (email) => {
    try {
      const result = await authService.forgotPassword(email);
      return result;
    } catch (error) {
      return { success: false, error: { message: error.message } };
    }
  };

  // Reset password function
  const resetPassword = async (token, newPassword) => {
    try {
      const result = await authService.resetPassword(token, newPassword);
      return result;
    } catch (error) {
      return { success: false, error: { message: error.message } };
    }
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const result = await authService.refreshAccessToken();
      
      if (result.success) {
        const newToken = authService.getAccessToken();
        const newRefreshToken = authService.getRefreshToken();
        
        dispatch({
          type: AUTH_ACTIONS.TOKEN_REFRESH,
          payload: {
            token: newToken,
            refreshToken: newRefreshToken
          }
        });
        return { success: true };
      } else {
        // Refresh failed, logout user
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        return { success: false, error: result.error };
      }
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      return { success: false, error: error.message };
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Update user data
  const updateUser = (userData) => {
    dispatch({
      type: AUTH_ACTIONS.SET_USER,
      payload: { user: userData }
    });
  };

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!state.user || !state.user.permissions) return false;
    return state.user.permissions.includes(permission);
  };

  // Check if user has specific role
  const hasRole = (role) => {
    if (!state.user || !state.user.role) return false;
    return state.user.role === role || (Array.isArray(state.user.roles) && state.user.roles.includes(role));
  };

  // Get user's subscription level
  const getSubscriptionLevel = () => {
    return state.user?.subscription?.level || 'free';
  };

  // Check if user is premium
  const isPremium = () => {
    const level = getSubscriptionLevel();
    return ['premium', 'enterprise'].includes(level);
  };

  // Activity tracking
  const updateActivity = () => {
    if (state.isAuthenticated) {
      dispatch({
        type: AUTH_ACTIONS.TOKEN_REFRESH,
        payload: {
          token: state.token,
          refreshToken: state.refreshToken
        }
      });
    }
  };

  // Context value
  const contextValue = {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    token: state.token,
    refreshToken: state.refreshToken,
    lastActivity: state.lastActivity,

    // Actions
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    refreshAccessToken: refreshToken,
    clearError,
    updateUser,
    updateActivity,

    // Utility functions
    hasPermission,
    hasRole,
    getSubscriptionLevel,
    isPremium,

    // Service access
    authService
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

// Higher-order component for protected routes
export const withAuth = (WrappedComponent) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          Loading...
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to login or show login prompt
      window.location.href = '/login?message=login-required';
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};

// Authentication guard component
export const AuthGuard = ({ children, fallback = null, requireAuth = true }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return fallback || (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px' 
      }}>
        Loading...
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return fallback;
  }

  if (!requireAuth && isAuthenticated) {
    return fallback;
  }

  return children;
};

export default AuthContext;
